import sparkTheme from '@/themes/sparkTheme';
import sleepioTheme from '@/themes/sleepioTheme';
import daylightTheme from '@/themes/daylightTheme';

import {
  SLEEPIO_PDT_UID, SPARKRX_PDT_UID, DAYLIGHT_PDT_UID, LineOfBusiness,
} from '@/utils/constants';

export type Product = 'sleepio' | 'sleepio-canopy' | 'spark' | 'daylight';

export type OnboardingParams = {
  slug: string;
  product: Product;
}

export enum Platform {
  Android = 'Android',
  iOS = 'iOS',
  Web = 'Web'
}

export const isSpark = (product?: string): boolean => {
  const p = (product ?? '')
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const SPARK_NAMES = new Set([
    'spark',
    'spark direct',
    'spark wellness',
    'spark 2.0',
  ]);

  return SPARK_NAMES.has(p);
};

export const isDaylight = (product?: string): boolean => {
  const p = (product ?? '').toLowerCase().trim();
  return p === 'daylight';
};

// Daylight has no coverage copy of its own, so it reuses Sleepio's
export const getCoverageCopyProduct = (product: Product) => (isDaylight(product) ? 'sleepio' : product);

type NormalizedProduct = 'sleepio' | 'spark' | 'daylight';

const normalizeProductForSwitch = (p: Product): NormalizedProduct => {
  if (isSpark(p)) { return 'spark'; }
  if (isDaylight(p)) { return 'daylight'; }
  return 'sleepio';
};

export const getProductName = (product: Product) => {
  switch (normalizeProductForSwitch(product)) {
  case 'spark':
    return 'Spark Direct';
  case 'daylight':
    return 'Daylight';
  default:
    return 'Sleepio';
  }
};

export const getPdtUid = (product: Product) => {
  switch (normalizeProductForSwitch(product)) {
  case 'spark':
    return SPARKRX_PDT_UID;
  case 'daylight':
    return DAYLIGHT_PDT_UID;
  default:
    return SLEEPIO_PDT_UID;
  }
};

export const getProductId = (product: Product) => {
  switch (normalizeProductForSwitch(product)) {
  case 'spark':
    return 4;
  case 'daylight':
    return 7;
  default:
    return 1;
  }
};

export const getTheme = (product: Product) => {
  switch (normalizeProductForSwitch(product)) {
  case 'spark':
    return sparkTheme;
  case 'daylight':
    return daylightTheme;
  default:
    return sleepioTheme;
  }
};

export const getProductEmail = (product: Product) => {
  switch (normalizeProductForSwitch(product)) {
  case 'spark':
    return 'spark@bighealth.com';
  case 'daylight':
    return 'daylight@bighealth.com';
  default:
    return 'sleepio@bighealth.com';
  }
};

export const getProductSupportEmail = (product: Product) => {
  switch (normalizeProductForSwitch(product)) {
  case 'spark':
    return 'spark@bighealth.com';
  case 'daylight':
    return 'daylight@bighealth.com';
  default:
    return 'hello@sleepio.com';
  }
};

// eslint-disable-next-line max-len
const SPRING_HEALTH_SUPPORT_URL = 'https://careteam.springhealth.com/hc/en-us/requests/new?ticket_form_id=14448838263956';
const SPRING_HEALTH_SUPPORT_LABEL = 'springhealth.com/support';

export const getProductSupportLink = (
  product: Product,
): { url: string; label: string } | undefined => {
  switch (normalizeProductForSwitch(product)) {
  case 'daylight':
  case 'sleepio':
    return { url: SPRING_HEALTH_SUPPORT_URL, label: SPRING_HEALTH_SUPPORT_LABEL };
  default:
    return undefined;
  }
};

export const getProductTermsAndConditions = (product: Product) => {
  switch (normalizeProductForSwitch(product)) {
  case 'spark':
    return 'https://www.bighealth.com/spark-direct-terms-and-conditions/';
  case 'daylight':
    return 'https://privacy.bighealth.com'; // TODO: Confirm Daylight-specific URL
  default:
    return 'https://privacy.bighealth.com';
  }
};

export const getProductPrivacyPolicy = (product: Product) => {
  switch (normalizeProductForSwitch(product)) {
  case 'spark':
    return 'https://www.bighealth.com/spark-direct-privacy-policy/';
  case 'daylight':
    return 'https://privacy.bighealth.com/policies'; // TODO: Confirm Daylight-specific URL
  default:
    return 'https://privacy.bighealth.com/policies';
  }
};

export const getProductMinAge = (product: Product) => {
  switch (normalizeProductForSwitch(product)) {
  case 'spark':
    return 13;
  case 'daylight':
    return 18;
  default:
    return 18;
  }
};

export const getProductKey = (product: Product): string => normalizeProductForSwitch(product);

/**
 * Determine line of business from flow context.
 * Provider flow (has organizationSlug) = rx, everything else = wellness.
 */
export const getLob = (organizationSlug?: string | null): LineOfBusiness => (
  organizationSlug ? 'wellness' : 'rx'
);

export const getDeepLinkScheme = (product: Product): string => {
  switch (normalizeProductForSwitch(product)) {
  case 'spark':
    return 'spark';
  default:
    return 'sleepio';
  }
};

export const setSiteTitleAndIcon = (product: Product) => {
  const normalized = normalizeProductForSwitch(product);
  const titleMap: Record<NormalizedProduct, string> = {
    spark: 'Spark Coverage Portal',
    daylight: 'Daylight Coverage Portal',
    sleepio: 'Sleepio Coverage Portal',
  };
  const faviconMap: Record<NormalizedProduct, string> = {
    spark: 'favicon-spark.png',
    daylight: 'favicon-daylight.png', // TODO: Add actual Daylight favicon
    sleepio: 'favicon-sleepio.png',
  };
  document.title = titleMap[normalized];
  const favicon = document.querySelector("link[rel~='icon']");
  const baseUrl = process.env.PUBLIC_URL;
  favicon?.setAttribute('href', `${baseUrl}/${faviconMap[normalized]}`);
};
